// eslint-disable-next-line unicorn/prefer-node-protocol
import { parse } from 'url'

import * as Sentry from '@sentry/react'
import { Extras } from '@sentry/types'
import { get } from 'lodash'

/**
 * Before creating a breadcrum will filter out non workgrid breakcrumbs, in case there are sensitive non-workgrid urls
 */
const beforeBreadcrumb: Sentry.BrowserOptions['beforeBreadcrumb'] = crumb => {
  if (crumb.data?.from) crumb.data.from = crumb.data.from.replace(/#id_token=.+$/, '#id_token=token')
  if (crumb.data?.to) crumb.data.to = crumb.data.to.replace(/#id_token=.+$/, '#id_token=token')

  return crumb
}

export const captureMessage = (message: string, extraInfo?: Extras) => {
  const { hostname, path } = parse(window.location.href)
  Sentry.withScope(scope => {
    scope.setTag('host', hostname)
    scope.setTag('path', path)
    if (extraInfo) scope.setExtras(extraInfo)
    Sentry.captureMessage(message)
  })
}

export const captureException = (exception: any, extraInfo?: Extras) => {
  const { hostname, path } = parse(window.location.href)
  Sentry.withScope(scope => {
    scope.setTag('host', hostname)
    scope.setTag('path', path)
    if (extraInfo) scope.setExtras(extraInfo)
    Sentry.captureException(exception)
  })
}

/**
 * Sets the global scope of sentry session
 */
export const setSentryGlobalScope = (user: any = {}, organization: any = {}, spaceId = '') => {
  Sentry.configureScope(scope => {
    scope.setUser({ id: user.id })
    scope.setTags({
      spaceId: spaceId,
      tenantId: organization.id,
      companyCode: organization.companyCode
    })

    if (user.roles) {
      const roles = [
        ...get(user.roles, ['organization', organization.id], []),
        ...get(user.roles, ['spaces', spaceId], [])
      ]

      scope.setExtra(
        'User roles for space',
        roles.map(role => role.name)
      )
    }
  })
}

/**
 * Initializes sentry - can override config if need be
 */

export const sentryInit = (config: Partial<Sentry.BrowserOptions> = {}) => {
  const defaultSettings: Sentry.BrowserOptions = {
    dsn: 'https://7d2c855d7a714f8dad1422a6d94c7330@sentry.io/1505386',
    integrations: [new Sentry.Integrations.Breadcrumbs({ console: false /*, dom: false*/ })], // this setting crashes the entire app...

    release: process.env.SENTRY_RELEASE,
    environment: (() => {
      const environments = {
        development: /\.dev\.workgrid\.com$/,
        test: /\.test\.workgrid\.com$/,
        production: /\.workgrid\.com$/,
        localhost: /^localhost$/,
        unknown: /./
      }

      for (const [environment, pattern] of Object.entries(environments)) {
        if (pattern.test(window.location.hostname)) return environment
      }
    })(),

    beforeBreadcrumb: beforeBreadcrumb,

    ignoreErrors: [
      // The general consensus is that these errors are benign and there is very little that can be done about them...
      'ResizeObserver loop completed with undelivered notifications.',
      'ResizeObserver loop limit exceeded'
    ]
  }

  Sentry.init({ ...defaultSettings, ...config })
}
